import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Skill {
  id: string;
  name: string;
  category: string;
  emoji: string;
}

export interface UserSkill {
  id: string;
  skill: Skill;
}

export const useSkills = (userId?: string) => {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [teachingSkills, setTeachingSkills] = useState<UserSkill[]>([]);
  const [learningSkills, setLearningSkills] = useState<UserSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        // Fetch all skills
        const { data: skillsData, error: skillsError } = await supabase
          .from('skills')
          .select('*')
          .order('name');

        if (skillsError) throw skillsError;
        setAllSkills(skillsData || []);

        // Fetch user's teaching skills
        if (targetUserId) {
          const { data: teachingData, error: teachingError } = await supabase
            .from('user_skills')
            .select('id, skill:skills(*)')
            .eq('user_id', targetUserId);

          if (teachingError) throw teachingError;
          setTeachingSkills(teachingData || []);

          // Fetch user's learning goals
          const { data: learningData, error: learningError } = await supabase
            .from('user_learning')
            .select('id, skill:skills(*)')
            .eq('user_id', targetUserId);

          if (learningError) throw learningError;
          setLearningSkills(learningData || []);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSkills();

    // Set up realtime subscriptions for user skills
    if (!targetUserId) return;

    const teachingChannel = supabase
      .channel(`user-skills-changes-${targetUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_skills',
          filter: `user_id=eq.${targetUserId}`,
        },
        async () => {
          // Refetch teaching skills
          const { data } = await supabase
            .from('user_skills')
            .select('id, skill:skills(*)')
            .eq('user_id', targetUserId);
          setTeachingSkills(data || []);
        }
      )
      .subscribe();

    const learningChannel = supabase
      .channel(`user-learning-changes-${targetUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_learning',
          filter: `user_id=eq.${targetUserId}`,
        },
        async () => {
          // Refetch learning skills
          const { data } = await supabase
            .from('user_learning')
            .select('id, skill:skills(*)')
            .eq('user_id', targetUserId);
          setLearningSkills(data || []);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(teachingChannel);
      supabase.removeChannel(learningChannel);
    };
  }, [targetUserId]);

  const addTeachingSkill = async (skillId: string) => {
    if (!user) return { error: 'Not authenticated' };

    try {
      const { error } = await supabase
        .from('user_skills')
        .insert({ user_id: user.id, skill_id: skillId });

      if (error) throw error;
      return { error: null };
    } catch (err: any) {
      return { error: err.message };
    }
  };

  const addLearningSkill = async (skillId: string) => {
    if (!user) return { error: 'Not authenticated' };

    try {
      const { error } = await supabase
        .from('user_learning')
        .insert({ user_id: user.id, skill_id: skillId });

      if (error) throw error;
      return { error: null };
    } catch (err: any) {
      return { error: err.message };
    }
  };

  return {
    allSkills,
    teachingSkills,
    learningSkills,
    loading,
    error,
    addTeachingSkill,
    addLearningSkill,
  };
};
